import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import createPersistedState from 'use-persisted-state';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { Table } from 'semantic-ui-react';
import styled from 'styled-components';
import get from 'lodash/get';
import { history } from 'nallian-shared-ui/lib/config/history';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import SecurityContextService from 'nallian-shared-ui/lib/services/securityContextService';
import IdentityService from 'nallian-shared-ui/lib/services/identityService';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { Button, Flex, Divider, ConfirmModal } from 'nallian-shared-ui/lib/components';
import {
  GetUserId,
  GetUserAccessToken,
  GetCommunityTag,
  GetRights,
} from 'nallian-shared-ui/lib/selectors';
import unlinkIdentityService from '../../../services/principals/unlinkIdentityService';
import linkIdentityService from '../../../services/principals/linkIdentityService';
import { PRINCIPAL_STATES } from '../../../constants';
import TypeAheadLink from './TypeAheadLink';

const LINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN = 'identities/link/non-system-organisation';
const LINK_IDENTITY_RIGHTS_SUPPORT = 'identities/link/my-identity';
const LINK_SYSTEM_ORG = 'identities/link/system-organisation';

const UNLINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN = 'identities/unlink/non-system-organisation';
const UNLINK_IDENTITY_RIGHTS_SUPPORT = 'identities/unlink/my-identity';
const UNLINK_SYSTEM_ORG = 'identities/unlink/system-organisation';

const LINK_IDENTITY_RIGHTS = [
  LINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN,
  LINK_IDENTITY_RIGHTS_SUPPORT,
  LINK_SYSTEM_ORG,
];

const UNLINK_IDENTITY_RIGHTS = [
  UNLINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN,
  UNLINK_IDENTITY_RIGHTS_SUPPORT,
  UNLINK_SYSTEM_ORG,
];

const IdentityInfo = styled.span`
  font-size: 0.9rem;
  opacity: 0.8;
  margin-top: 0.2rem;
`;

const usePrincipalState = createPersistedState('principalId');

const PrincipalIdentities = ({
  identities,
  dispatchUnlinkIdentityDelete,
  unlinkIdentityFetch,
  linkIdentityFetch,
  dispatchLinkIdentityPost,
  isSystemOrg,
  dispatchSecurityContextGet,
  dispatchMyIdentityGet,
  data,
}) => {
  const { t } = useTranslation();
  const { actionId } = useParams();
  const location = useLocation();

  const myIdentityId = useSelector(GetUserId);
  const rights = useSelector(GetRights);
  const accessToken = useSelector(GetUserAccessToken);
  const communityTag = useSelector(GetCommunityTag);

  // eslint-disable-next-line no-unused-vars
  const [principalId, setPrincipalId] = usePrincipalState(undefined);

  const handleRefetchSecurityContext = useCallback(() => {
    history.replace({ pathname: location.pathname, search: 'redirectTab=identities' });
    dispatchSecurityContextGet(undefined, principalId, communityTag, accessToken);
    dispatchMyIdentityGet(accessToken);
  }, [
    location.pathname,
    dispatchMyIdentityGet,
    dispatchSecurityContextGet,
    accessToken,
    communityTag,
    principalId,
  ]);

  const handleUnlinkIdentity = useCallback(
    identityId => {
      history.replace({ pathname: location.pathname, search: 'redirectTab=identities' });
      dispatchUnlinkIdentityDelete(actionId, identityId, handleRefetchSecurityContext, t);
    },
    [actionId, location.pathname, dispatchUnlinkIdentityDelete, handleRefetchSecurityContext, t]
  );

  const handleLinkIdentity = useCallback(() => {
    history.replace({ pathname: location.pathname, search: 'redirectTab=identities' });

    dispatchLinkIdentityPost(
      actionId,
      { identityId: myIdentityId },
      handleRefetchSecurityContext,
      t
    );
  }, [
    dispatchLinkIdentityPost,
    location.pathname,
    myIdentityId,
    actionId,
    handleRefetchSecurityContext,
    t,
  ]);

  const alreadyLinked = useMemo(() => identities.find(i => i.identity?.id === myIdentityId), [
    identities,
    myIdentityId,
  ]);

  return (
    <Flex fluid direction="column">
      {identities?.length > 0 ? (
        <Table basic="very">
          <Table.Body>
            {identities.map(i => (
              <Table.Row key={`identity_${i.id}`}>
                <Table.Cell width={12}>
                  {i.identity && (
                    <Flex direction="column">
                      <strong>
                        {i.identity.fullName || i.identity.id}{' '}
                        {myIdentityId.toLowerCase() === i.identity.id.toLowerCase() &&
                          `(${t('myIdentity')})`}
                      </strong>
                      <IdentityInfo>
                        {i.identity.emailAddress}
                        {i.identity.emailAddress && i.identity.phoneNumber && ` - `}
                        {i.identity.phoneNumber}{' '}
                        {`(${get(
                          i.identity,
                          'identityProvider.name',
                          t('unknownIdentityProvider')
                        )})`}
                      </IdentityInfo>
                    </Flex>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <HasSomeRights right={UNLINK_IDENTITY_RIGHTS}>
                    {isSystemOrg ? (
                      <HasRight right={UNLINK_SYSTEM_ORG}>
                        <ConfirmModal
                          header={t('areYouSureYouWantToUnlinkIdentity')}
                          cancelContent={t('cancel')}
                          confirmContent={t('unlink')}
                          confirmColor="red"
                          onConfirm={() => handleUnlinkIdentity(i.id)}
                          identityId={i.id}
                          trigger={
                            <Button
                              basic
                              loading={
                                unlinkIdentityFetch.pending &&
                                unlinkIdentityFetch.meta?.identityId === i.id
                              }
                              icon="unlinkify"
                              content={t('unlink')}
                            />
                          }
                        />
                      </HasRight>
                    ) : (
                      <HasSomeRights
                        rights={[
                          UNLINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN,
                          UNLINK_IDENTITY_RIGHTS_SUPPORT,
                        ]}
                      >
                        {(hasRight(rights, UNLINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN) ||
                          (hasRight(rights, UNLINK_IDENTITY_RIGHTS_SUPPORT) &&
                            myIdentityId === i.identity?.id)) && (
                          <ConfirmModal
                            header={t('areYouSureYouWantToUnlinkIdentity')}
                            cancelContent={t('cancel')}
                            confirmContent={t('unlink')}
                            confirmColor="red"
                            onConfirm={() => handleUnlinkIdentity(i.id)}
                            identityId={i.id}
                            trigger={
                              <Button
                                basic
                                loading={
                                  unlinkIdentityFetch.pending &&
                                  unlinkIdentityFetch.meta?.identityId === i.id
                                }
                                icon="unlinkify"
                                content={t('unlink')}
                              />
                            }
                          />
                        )}
                      </HasSomeRights>
                    )}
                  </HasSomeRights>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      ) : (
        <Flex fluid direction="column">
          <strong>{t('noLinkedAccountsYet')}</strong>
        </Flex>
      )}
      <HasSomeRights rights={LINK_IDENTITY_RIGHTS}>
        <Flex direction="column" fluid>
          {!isSystemOrg ? (
            <HasSomeRights
              rights={[LINK_IDENTITY_RIGHTS_SUPPORT, LINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN]}
            >
              <Flex fluid direction="column">
                <HasRight right={LINK_IDENTITY_RIGHTS_GLOBAL_COMMUNITY_ADMIN}>
                  <Divider />
                  <TypeAheadLink
                    disabled={
                      linkIdentityFetch.pending || data?.activeState !== PRINCIPAL_STATES.ACTIVE
                    }
                    principalId={actionId}
                    handleRefetchSecurityContext={handleRefetchSecurityContext}
                  />
                </HasRight>
                {!alreadyLinked && (
                  <>
                    <Divider />
                    <Flex fluid justifyContent="flex-end">
                      <Button
                        btnprimary
                        onClick={handleLinkIdentity}
                        icon="linkify"
                        loading={linkIdentityFetch.pending}
                        disabled={
                          linkIdentityFetch.pending || data?.activeState !== PRINCIPAL_STATES.ACTIVE
                        }
                        content={t('linkMyIdentityToPrincipal')}
                      />
                    </Flex>
                  </>
                )}
              </Flex>
            </HasSomeRights>
          ) : (
            <HasRight right={LINK_SYSTEM_ORG}>
              <Divider />
              <TypeAheadLink
                principalId={actionId}
                disabled={
                  linkIdentityFetch.pending || data?.activeState !== PRINCIPAL_STATES.ACTIVE
                }
                handleRefetchSecurityContext={handleRefetchSecurityContext}
              />
              {!alreadyLinked && (
                <>
                  <Divider />
                  <Flex fluid justifyContent="flex-end">
                    <Button
                      btnprimary
                      onClick={handleLinkIdentity}
                      icon="linkify"
                      loading={linkIdentityFetch.pending}
                      disabled={
                        linkIdentityFetch.pending || data?.activeState !== PRINCIPAL_STATES.ACTIVE
                      }
                      content={t('linkMyIdentityToPrincipal')}
                    />
                  </Flex>
                </>
              )}
            </HasRight>
          )}
        </Flex>
      </HasSomeRights>
    </Flex>
  );
};

PrincipalIdentities.propTypes = {
  dispatchMyIdentityGet: PropTypes.func.isRequired,
  dispatchSecurityContextGet: PropTypes.func.isRequired,
  securityContextFetch: PropTypes.object.isRequired,
  dispatchLinkIdentityPost: PropTypes.func.isRequired,
  linkIdentityFetch: PropTypes.object.isRequired,
  dispatchUnlinkIdentityDelete: PropTypes.func.isRequired,
  unlinkIdentityFetch: PropTypes.object.isRequired,
  isSystemOrg: PropTypes.bool.isRequired,
  identities: PropTypes.array,
  data: PropTypes.object,
  isActive: PropTypes.bool,
};

PrincipalIdentities.defaultProps = { identities: [], data: undefined, isActive: false };

export default compose(
  linkIdentityService,
  unlinkIdentityService,
  SecurityContextService,
  IdentityService
)(PrincipalIdentities);
